import React, { Dispatch, memo, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import styles from './styles.module.scss';
import { loanStatusMapping } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/utils/LoanStatusMapping';
import { useTranslation } from 'react-i18next';
import { LoanStatusType } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import {
  getEmptyState,
  getLoanStatusLabel
} from '../HbtMasterPoolDetails/utils/masterPoolDetailsUtils';
import ForceStatusFlyout from '../HbtMasterPool/component/ForceStatusFlyout';
import HistoricalDataFlyout from '../HbtMasterPoolLoansInventoryTable/components/HistoricalDataFlyout/HistoricalDataFlyout';
import { LoanInventoryTableFields } from '../HbtMasterPoolLoansInventoryTable/types';
import { useLocation } from 'react-router-dom';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { clearSessionStorage } from 'Feature/V2/Shared_Portfolio/utils/clearSessionStorage';
import { config } from '../../../../../config';
import moment from 'moment';
import { dropdownIdsApprovedStatus, dropdownIdsWithdrawnStatus } from '../../constants';
import { MasterPoolLoanStatus } from '../HbtMasterPoolTable/utils/LoanStatusMapping';
import { ToastNotificationConfig } from '../HbtMasterPoolDetails/types';
import LoanStatusPill from '../../shared/LoanStatusPill';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';
import i18n from 'i18next';
import { MasterPoolLoanDetails } from './types';

interface LoanDetailsHeaderProps {
  masterPoolNumber?: number;
  loanReferenceNumber: string;
  loanStatus: LoanStatusType;
  loanPortfolioEpipProcessingStatus: string | undefined;
  setModifyFlyout: (value: React.SetStateAction<boolean>) => void;
  setOverviewFlyout: (value: React.SetStateAction<boolean>) => void;
  cmhcAccountNumber: number;
  loanInventoryData: LoanInventoryTableFields;
  loanDetailsData: MasterPoolLoanDetails;
  approvalDate?: string;
  cancellationDate?: string;
  setMasterPoolNotification?: (show: boolean) => void;
  setNotificationBannerConfig?: Dispatch<SetStateAction<ToastNotificationConfig>>;
  setIsForceStatusTriggered?: (isTriggered: boolean) => void;
}

const LoanDetailsHeader = ({
  cmhcAccountNumber,
  setModifyFlyout,
  setOverviewFlyout,
  masterPoolNumber,
  loanReferenceNumber,
  loanStatus,
  loanPortfolioEpipProcessingStatus,
  loanInventoryData,
  loanDetailsData,
  approvalDate,
  cancellationDate,
  setMasterPoolNotification,
  setNotificationBannerConfig,
  setIsForceStatusTriggered
}: LoanDetailsHeaderProps) => {
  const masterPoolTitleRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { t: mp } = useTranslation('HbtMasterPool');
  const [openForceStatusFlyout, setOpenForceStatusFlyout] = React.useState<boolean>(false);
  const [openAddRemarksFlyout, setOpenAddRemarksFlyout] = React.useState<boolean>(false);
  const [openHistoricalFlyout, setOpenHistoricalFlyout] = React.useState<boolean>(false);
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });
  const modifiedLoan = 1;
  const modified =
    loanInventoryData.riskResultOverrideIndicator &&
    Number(loanInventoryData.riskResultOverrideIndicator) === modifiedLoan
      ? true
      : false;

  const location = useLocation();
  const { dataType } = getSearchParams(location.search);
  const loansUpdateStatusUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/status-update`;

  useEffect(() => {
    const targetElement = document.getElementById('masterPoolTitleRef');
    if (targetElement) {
      if (!sessionStorage.getItem('searchLoanDetail')) return;
      if (dataType === 'Lender Reference Number' || dataType === 'CMHC Account Number') {
        setTimeout(() => {
          const top = targetElement.getBoundingClientRect().top + window.screenY - 20;
          window.scrollTo({ top, behavior: 'smooth' });
          clearSessionStorage('searchLoanDetail');
        }, 500);
      }
    }
  }, []);

  const formatToDateOnly = (rawTimestamp: string): string => {
    const [datePart] = rawTimestamp.split(' ');
    return datePart;
  };

  const callToaster = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  let dropDownOptionsForUpdateRemarks = [
    {
      ids: dropdownIdsWithdrawnStatus,
      groupName: ''
    }
  ];
  const isApprovedStatus =
    loanInventoryData && loanInventoryData.loanStatus === MasterPoolLoanStatus.Approved;
  if (isApprovedStatus) {
    dropDownOptionsForUpdateRemarks = [
      {
        ids: dropdownIdsApprovedStatus,
        groupName: ''
      }
    ];
  }
  const loansUpdateRemarkUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/remarks-update`;
  const latestRemark = loanDetailsData.latestRemark;

  return (
    <>
      <ForceStatusFlyout
        openFlyout={openForceStatusFlyout}
        toggleDrawer={setOpenForceStatusFlyout}
        headerText={t('Update-Loan-Status-MasterPool-Loans-Inventory-Header-Text')}
        loans={String(cmhcAccountNumber)}
        masterPoolCmhcAccountNumber={masterPoolNumber != null ? String(masterPoolNumber) : ''}
        isFromLoansInventory={true}
        radioButtonOptions={[
          { label: t('Approved'), value: '06' },
          { label: t('Withdrawn'), value: '05' }
        ]}
        requestUrl={loansUpdateStatusUrl}
        typeOfFlyout={'changeStatus'}
        setNotificationBannerConfig={setNotificationBannerConfig}
        setMasterPoolNotification={setMasterPoolNotification}
        setIsForceStatusTriggered={setIsForceStatusTriggered}
        callToaster={callToaster}
      />

      <ForceStatusFlyout
        callToaster={callToaster}
        openFlyout={openAddRemarksFlyout}
        toggleDrawer={setOpenAddRemarksFlyout}
        loans={String(cmhcAccountNumber)}
        loansData={[loanInventoryData]}
        masterPoolCmhcAccountNumber={masterPoolNumber != null ? String(masterPoolNumber) : ''}
        remarksDropDownHeader={dropDownOptionsForUpdateRemarks}
        isFromLoansInventory={true}
        radioButtonOptions={[
          { label: t('Approved'), value: '06' },
          { label: t('Withdrawn'), value: '05' }
        ]}
        requestUrl={loansUpdateRemarkUrl}
        typeOfFlyout={'updateRemark'}
        flyoutTitle={t('Add-Remarks-Flyout-Heading')}
        setNotificationBannerConfig={setNotificationBannerConfig}
        setMasterPoolNotification={setMasterPoolNotification}
        setIsForceStatusTriggered={setIsForceStatusTriggered}
        headerText={t('Add-Remarks-Flyout-Heading-Desc')}
        errorModalHeader={t('Error-Modal-Header')}
        textFieldButtonText={t('Add-CMHC-Account-Numbers')}
      />
      <HistoricalDataFlyout
        openFlyout={openHistoricalFlyout}
        toggleFlyout={() => setOpenHistoricalFlyout(false)}
        selectedRowsData={[loanInventoryData]}
      />

      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>

      <Box className={styles.boxShadowContainer}>
        <Stack direction="row" className={styles.titleContainer}>
          <Box className={styles.FirstRow}>
            <Stack direction="row" className={styles.topContent} justifyContent="space-between">
              <Stack direction="column" ref={masterPoolTitleRef}>
                <Typography className={styles.portfolioId} variant="h4" id="masterPoolTitleRef">
                  {t('Master-Pool-Number')}: {masterPoolNumber}
                </Typography>
                <Stack direction="row" className={styles.cmhcContainer}>
                  <Typography className={styles.cmhcNumber} variant="h4">
                    {t('CMHC-Account-Number')} {cmhcAccountNumber}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center">
                    <LoanStatusPill
                      status={loanStatus}
                      label={getLoanStatusLabel(loanStatus as keyof typeof loanStatusMapping, mp)}
                      filled={!modified}
                      modified={modified}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Box className={styles.SecondRow}>
                <Stack direction="row" spacing={3} className={styles.buttonsContainer}>
                  {loanInventoryData.remarks.length > 0 && (
                    <Button
                      name="viewHistoricalData"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => {
                        setOpenHistoricalFlyout(true);
                      }}
                      text={{
                        value: mp('View-Historical-Data')
                      }}
                    />
                  )}
                  <Button
                    name="addModifyRemark"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      setOpenAddRemarksFlyout(true);
                    }}
                    text={{
                      value: t('Add-Remarks')
                    }}
                  />
                  <Button
                    name="OverrideLoanStatusBtn"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.SMALL}
                    style={{
                      backgroundColor: '#3F51B5',
                      color: '#fff',
                      textTransform: 'none'
                    }}
                    onClick={() => {
                      setOpenForceStatusFlyout(true);
                    }}
                    text={{
                      value: mp('Override-Loan-Status')
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" className={styles.masterPoolDetailsContainer}>
              <Stack direction="column">
                <Typography className={styles.masterPoolDetails} variant="h6">
                  {t('Lender-Reference-Number')}
                </Typography>
                <Typography className={styles.masterPoolDetailsData} variant="h4">
                  {loanReferenceNumber}
                </Typography>
              </Stack>
              <Stack direction="column">
                <Typography className={styles.masterPoolDetails} variant="h6">
                  {t('Approved-Date')}
                </Typography>
                <Typography className={styles.masterPoolDetailsData} variant="h4">
                  {approvalDate ? moment.utc(approvalDate).format('YYYY-MM-DD') : getEmptyState()}
                </Typography>
              </Stack>
              <Stack direction="column">
                <Typography className={styles.masterPoolDetails} variant="h6">
                  {t('Cancellation-Date')}
                </Typography>
                <Typography className={styles.masterPoolDetailsData} variant="h4">
                  {cancellationDate
                    ? moment.utc(cancellationDate).format('YYYY-MM-DD')
                    : getEmptyState()}
                </Typography>
              </Stack>
            </Stack>
            {latestRemark && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: 'start',
                  alignItems: 'start'
                }}
                className={styles.remarkAddedContainer}
              >
                <Tooltip
                  title={t('Remarks-Table-Header-Tooltip')}
                  placement="top-start"
                  arrow
                  slotProps={{
                    tooltip: {
                      className: styles.requestOverviewDetailsToolTip
                    },
                    arrow: {
                      className: styles.tooltipArrow
                    }
                  }}
                >
                  <div>
                    <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                  </div>
                </Tooltip>
                <Stack direction="row" spacing={1} className={styles.remarksAdded}>
                  <Typography>
                    <span className={styles.remark}>
                      {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified')}
                    </span>
                    <span className={styles.title}>
                      {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified-Title', {
                        remark: isRemarkOther({
                          remarkID: latestRemark.remarkId,
                          remarkMessage: ''
                        })
                          ? latestRemark.remarkText
                          : i18n.language === 'en'
                            ? latestRemark.rationaleEnglish
                            : latestRemark.rationaleFrench
                      })}
                    </span>
                    <span className={styles.date}>
                      {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified-Date', {
                        date: formatToDateOnly(latestRemark.recordTimestamp),
                        fName: latestRemark.firstName ?? '',
                        lName: latestRemark.lastName ?? ''
                      })}
                    </span>
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default memo(LoanDetailsHeader);
